/**
 * features/reports/GeneratedBatchesList.tsx
 * ------------------------------------------------------------------
 * Shared "already generated — who & when" list used by Generate, View and
 * Send Reports. Shows each generated class with its report count, who
 * generated it and when. Each screen supplies its own per-row action via
 * `renderAction` (Regenerate / View / Send).
 * ------------------------------------------------------------------ */
import type { ReactNode } from 'react';
import { CheckCircle2, Clock, FileText } from 'lucide-react';
import { Spinner } from '@/components/ui/primitives';
import { SoftCard } from '@/components/ui/glass';
import type { ReportBatch } from '@/types';

function whenLabel(iso: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('en-GB', {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  });
}

export default function GeneratedBatchesList({
  batches,
  loading,
  title = 'Generated reports',
  emptyText = 'No reports have been generated for this term yet.',
  renderAction,
  onRowClick,
  highlightId,
}: {
  batches: ReportBatch[];
  loading: boolean;
  title?: string;
  emptyText?: string;
  renderAction?: (batch: ReportBatch) => ReactNode;
  onRowClick?: (batch: ReportBatch) => void;
  highlightId?: string;
}) {
  if (loading) {
    return <SoftCard><Spinner label="Loading generated reports…" /></SoftCard>;
  }
  if (batches.length === 0) {
    return (
      <SoftCard hover={false}>
        <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
          <FileText size={16} className="text-gray-400" /> {emptyText}
        </div>
      </SoftCard>
    );
  }

  return (
    <SoftCard pad="p-0" hover={false}>
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100 dark:border-gray-700">
        <span className="text-sm font-semibold text-gray-800 dark:text-gray-100">
          {title} <span className="text-gray-400 font-normal">· {batches.length} class{batches.length === 1 ? '' : 'es'}</span>
        </span>
      </div>
      <div className="divide-y divide-gray-50 dark:divide-gray-700/50">
        {batches.map((b) => (
          <div
            key={b.id}
            onClick={onRowClick ? () => onRowClick(b) : undefined}
            className={`flex items-center justify-between gap-3 px-4 py-2.5 ${
              onRowClick ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/40' : ''
            } ${highlightId === b.id ? 'bg-blue-50/60 dark:bg-blue-900/20' : ''}`}
          >
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-medium text-sm text-gray-800 dark:text-gray-100">{b.className}</span>
                {b.status === 'ready' ? (
                  <CheckCircle2 size={13} className="text-emerald-500 shrink-0" />
                ) : (
                  <span className="text-[10px] uppercase tracking-wide text-amber-600">{b.status}</span>
                )}
                {b.flagOverride && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">flag override</span>
                )}
              </div>
              <div className="text-xs text-gray-500 flex items-center gap-2 mt-0.5">
                <span>{b.studentIds?.length ?? 0} reports</span>
                <span className="text-gray-300">·</span>
                <span className="inline-flex items-center gap-1"><Clock size={11} /> {whenLabel(b.generatedAt)}</span>
                <span className="text-gray-300">·</span>
                <span className="truncate">by {b.generatedByName || '—'}</span>
              </div>
            </div>
            {renderAction && <div className="shrink-0" onClick={(e) => e.stopPropagation()}>{renderAction(b)}</div>}
          </div>
        ))}
      </div>
    </SoftCard>
  );
}
